// src/services/expenses.ts
import api from "./api";
import {
  Expense,
  CreateExpenseData,
  UpdateExpenseData,
  ExpenseFilters,
  ExpensesResponse,
  ApprovalData,
} from "../types/expense";

class ExpenseService {
  // Get all expenses with filters and pagination
  async getExpenses(filters: ExpenseFilters = {}): Promise<ExpensesResponse> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const url = queryString ? `/expenses?${queryString}` : "/expenses";

    return await api.get(url);
  }

  // Get expense by ID
  async getExpenseById(
    id: string
  ): Promise<{ success: boolean; data: Expense }> {
    return await api.get(`/expenses/${id}`);
  }

  // Create new expense
  async createExpense(
    expenseData: CreateExpenseData
  ): Promise<{ success: boolean; data: Expense }> {
    try {
      return await api.post("/expenses", expenseData);
    } catch (error: any) {
      console.error("Error creating expense:", error);
      throw error; // Re-throw to handle in component
    }
  }

  // Update expense
  async updateExpense(
    id: string,
    expenseData: UpdateExpenseData
  ): Promise<{ success: boolean; data: Expense }> {
    try {
      return await api.put(`/expenses/${id}`, expenseData);
    } catch (error: any) {
      console.error("Error updating expense:", error);
      throw error; // Re-throw to handle in component
    }
  }

  // Delete expense
  async deleteExpense(
    id: string
  ): Promise<{ success: boolean; message: string }> {
    return await api.delete(`/expenses/${id}`);
  }

  // Update expense approval status
  async updateApprovalStatus(
    id: string,
    approvalData: ApprovalData
  ): Promise<{ success: boolean; data: Expense }> {
    return await api.put(`/expenses/${id}/approval`, approvalData);
  }

  // Get expense categories
  async getCategories(): Promise<{ success: boolean; data: string[] }> {
    return await api.get("/expenses/categories");
  }

  // Get expense summary/statistics
  async getSummary(
    filters: ExpenseFilters = {}
  ): Promise<{ success: boolean; data: any }> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const url = queryString
      ? `/expenses/summary?${queryString}`
      : "/expenses/summary";

    return await api.get(url);
  }

  // Get expense reports dashboard
  async getDashboard(): Promise<{ success: boolean; data: any }> {
    return await api.get("/expenses/reports/dashboard");
  }

  // Get expenses by category
  async getByCategory(): Promise<{ success: boolean; data: any }> {
    return await api.get("/expenses/reports/by-category");
  }

  // Get expense trends
  async getTrends(): Promise<{ success: boolean; data: any }> {
    return await api.get("/expenses/reports/trends");
  }

  // Get vendor analysis
  async getVendorAnalysis(): Promise<{ success: boolean; data: any }> {
    return await api.get("/expenses/reports/vendors");
  }

  // Export expense report
  async exportReport(filters: ExpenseFilters = {}): Promise<any> {
    const params = new URLSearchParams();

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, value.toString());
      }
    });

    const queryString = params.toString();
    const url = queryString
      ? `/expenses/reports/export?${queryString}`
      : "/expenses/reports/export";

    return await api.get(url);
  }

  // Get expense categories
  async getExpenseCategories(): Promise<{ success: boolean; data: string[] }> {
    return await api.get("/expenses/categories");
  }

  // Get expense summary
  async getExpenseSummary(): Promise<{ success: boolean; data: any }> {
    return await api.get("/expenses/summary");
  }
}

export const expenseService = new ExpenseService();
export default expenseService;
